import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  CssBaseline,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Toolbar,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { styled } from '@mui/system';
import SitemarkIcon from '../../components/SitemarkIcon';
import { useNavigate } from 'react-router-dom';
import ErrorDialog from '../../components/Dialogs/OnErrorDialog';

const Header = styled(AppBar)({
  backgroundColor: 'white',
  boxShadow: 'none',
  borderBottom: '1px solid #f0f2f5',
  padding: '12px 40px',
});

const SectionCard = styled(Paper)({
  padding: '24px 28px',
  borderRadius: 16,
  border: '1px solid #e7edf4',
  boxShadow: '0 1px 2px rgba(16, 20, 24, 0.04)',
  backgroundColor: 'white',
  marginBottom: 16,
});

const SectionIcon = styled(Box)({
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: '#eff4fb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#3f6ed8',
});

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.8}>
    <Typography fontSize="0.85rem" color="text.secondary">{label}</Typography>
    <Typography fontSize="0.9rem" fontWeight={500} color="#101418" textAlign="right" sx={{ maxWidth: '60%' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const SubmitOnboaringScreen = ({ form, setForm, onBack, onNext }) => {

  const navigate = useNavigate();
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorTerms, setErrorTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  const uploadOnboardingData = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Error al enviar los datos');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error al subir datos:', error);
      throw error;
    }
  };

  return (
    <Box sx={{ fontFamily: 'Inter, "Noto Sans", sans-serif', bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <CssBaseline />
      <Header position="sticky">
        <Toolbar sx={{ justifyContent: 'space-between', px: '0 !important' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box width={28} height={28}>
              <SitemarkIcon />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Paso 3 de 3
          </Typography>
        </Toolbar>
      </Header>

      <Container maxWidth="md" sx={{ py: 6 }}>

        <ErrorDialog
          open={errorOpen}
          onClose={() => setErrorOpen(false)}
          message={errorMessage} />

        <Box sx={{ mb: 5 }}>
          <Stepper activeStep={2} alternativeLabel>
            <Step><StepLabel>Tu empresa</StepLabel></Step>
            <Step><StepLabel>Módulos</StepLabel></Step>
            <Step><StepLabel>Revisar</StepLabel></Step>
          </Stepper>
        </Box>

        <Box textAlign="center" mb={5}>
          <Typography variant="h4" fontWeight={700} color="#101418" gutterBottom>
            Revisa antes de empezar
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540, mx: 'auto' }}>
            Confirma los datos ingresados. Podrás editarlos en cualquier momento desde Configuración.
          </Typography>
        </Box>

        <SectionCard elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <SectionIcon><BusinessIcon fontSize="small" /></SectionIcon>
            <Typography variant="h6" fontWeight={700} color="#101418">
              Información de la empresa
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <InfoRow label="Nombre" value={form.general?.companyName} />
          <InfoRow label="Identificación fiscal" value={form.general?.taxId} />
          <InfoRow label="Teléfono" value={form.general?.phone} />
          <InfoRow label="Dirección" value={form.general?.address} />
          <InfoRow label="País" value={form.general?.country} />
          <InfoRow label="Moneda" value={form.general?.currency} />
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.8}>
            <Typography fontSize="0.85rem" color="text.secondary">Tipo de financiera</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end" maxWidth="60%">
              {form.general?.companyTypes?.length > 0 ? (
                form.general.companyTypes.map((type, i) => (
                  <Chip key={i} label={type} size="small" sx={{ bgcolor: '#eff4fb', color: '#3f6ed8', fontWeight: 500 }} />
                ))
              ) : (
                <Typography fontSize="0.9rem" color="text.secondary">—</Typography>
              )}
            </Box>
          </Box>
        </SectionCard>

        <SectionCard elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <SectionIcon><ViewModuleIcon fontSize="small" /></SectionIcon>
            <Typography variant="h6" fontWeight={700} color="#101418">
              Módulos activados
            </Typography>
            <Chip
              label={`${form.selectedModules?.length || 0}`}
              size="small"
              sx={{ bgcolor: '#eff4fb', color: '#3f6ed8', fontWeight: 600, ml: 'auto !important' }}
            />
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Box display="flex" flexWrap="wrap" gap={0.8}>
            {form.selectedModules?.length > 0 ? (
              form.selectedModules.map((m, i) => (
                <Chip key={i} label={m} size="small" variant="outlined" sx={{ borderColor: '#e7edf4', color: '#101418' }} />
              ))
            ) : (
              <Typography fontSize="0.9rem" color="text.secondary">No se han seleccionado módulos</Typography>
            )}
          </Box>
        </SectionCard>

        <SectionCard elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <SectionIcon><StorefrontIcon fontSize="small" /></SectionIcon>
            <Typography variant="h6" fontWeight={700} color="#101418">
              Sucursal principal
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          {form.sucursales?.length > 0 ? (
            form.sucursales.map((branch, i) => (
              <Box key={i}>
                <InfoRow label="Nombre" value={branch.nombre} />
                <InfoRow label="Dirección" value={branch.direccion} />
                <InfoRow label="Contacto" value={branch.contacto} />
              </Box>
            ))
          ) : (
            <Typography fontSize="0.9rem" color="text.secondary">Sin sucursal</Typography>
          )}
        </SectionCard>

        <SectionCard elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <SectionIcon><PersonOutlineIcon fontSize="small" /></SectionIcon>
            <Typography variant="h6" fontWeight={700} color="#101418">
              Usuario administrador
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <InfoRow label="Nombre completo" value={form.usuarios?.administrador?.nombre_completo} />
          <InfoRow label="Nombre de usuario" value={form.usuarios?.administrador?.nombre} />
          <InfoRow label="Contraseña" value={form.usuarios?.administrador?.contrasena ? '••••••••' : ''} />
        </SectionCard>

        <Box mt={3} mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) setErrorTerms(false);
                }}
              />
            }
            label={
              <Typography fontSize="0.95rem" color="#101418">
                Estoy de acuerdo con los{' '}
                <Box component="span" sx={{ color: '#3f6ed8', fontWeight: 600, cursor: 'pointer' }}>
                  términos y condiciones
                </Box>
              </Typography>
            }
          />
          {errorTerms && (
            <Typography color="error" fontSize="0.85rem" mt={-0.5} ml={4}>
              Debes aceptar los términos y condiciones para continuar.
            </Typography>
          )}
        </Box>

        <Box mt={3}>
          <LinearProgress variant="determinate" value={100} sx={{ bgcolor: '#e7edf4', height: 6, borderRadius: 1 }} />
        </Box>

        <Stack direction="row" spacing={2} mt={3}>
          <Button
            onClick={() => { onBack() }}
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            disabled={submitting}
            sx={{ fontWeight: 600, flex: 1, py: 1.3, borderRadius: 2, textTransform: 'none' }}
          >
            Editar información
          </Button>

          <Button
            variant="contained"
            size="large"
            color="primarybutton"
            disabled={submitting}
            startIcon={<RocketLaunchIcon />}
            sx={{ fontWeight: 700, flex: 2, py: 1.3, borderRadius: 2, textTransform: 'none', fontSize: '1rem' }}
            onClick={async () => {
              try {
                if (!termsAccepted) {
                  setErrorTerms(true);
                  return;
                }
                setSubmitting(true);
                const payload = { ...form }; // Puedes transformar si es necesario
                const result = await uploadOnboardingData(payload);
                console.log('Subida exitosa:', result);
                navigate('/dashboard', { replace: true }); // Redirige al dashboard
              } catch (error) {
                setErrorMessage('No se pudo completar el proceso. Verifica tu conexión o los datos ingresados.');
                setErrorOpen(true);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? 'Creando tu cuenta...' : 'Empezar a usar Bankivo'}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default SubmitOnboaringScreen;
